/**
 * OmniLeagues — Trial countdown ribbon.
 *
 * Renders for every user on a `subscription_status === "trial"` account.
 * Shows days remaining until `trial_expires_at` and a single Upgrade CTA
 * that fires off a Stripe Checkout session. Expired trials get a
 * locked-out variant — the click-through still goes to billing so the AD
 * has a one-click path back to a paid plan.
 *
 * Wire-up:
 *   <TrialRibbon /> — placed once near the top of Dashboard. Self-fetches
 *   /api/auth/me so the parent Dashboard doesn't need to plumb props.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { AlertTriangle, Sparkles, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export default function TrialRibbon() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/auth/me`).then(({ data }) => {
      if (alive) setMe(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!me) return null;
  // Only render for trial-state accounts.
  if (me.subscription_status !== 'trial') return null;

  const remaining = daysLeft(me.trial_expires_at);
  const expired = remaining !== null && remaining <= 0;

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/billing/checkout/${me.id}`,
        {
          tier: 'starter',
          success_url: `${window.location.origin}/dashboard?upgrade=success`,
          cancel_url: `${window.location.origin}/dashboard?upgrade=cancel`,
        },
      );
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      // The simulated-session fallback returns a relative url which the
      // browser can't redirect to from cross-origin context — treat any
      // failure as a soft no-op.
    } finally {
      setBusy(false);
    }
  };

  if (expired) {
    return (
      <div
        data-testid="trial-ribbon-expired"
        className="w-full px-4 lg:px-6 pt-2"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-200">
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Your trial expired. Write actions are locked until you upgrade.
            </span>
          </div>
          <Button
            size="sm"
            onClick={startCheckout}
            disabled={busy}
            data-testid="trial-upgrade-cta"
            className="bg-rose-600 hover:bg-rose-500 text-white"
          >
            {busy ? 'Opening checkout…' : 'Upgrade now'}
          </Button>
        </div>
      </div>
    );
  }

  const tone =
    remaining <= 3
      ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
      : 'bg-blue-500/10 border-blue-500/30 text-blue-200';

  return (
    <div
      data-testid="trial-ribbon"
      className="w-full px-4 lg:px-6 pt-2"
    >
      <div
        className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 rounded-lg border ${tone}`}
      >
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 shrink-0" />
          <span>
            <strong>{remaining}</strong> day{remaining === 1 ? '' : 's'} left in your free trial.
            Pick a plan now to keep everything you've set up.
          </span>
        </div>
        <Button
          size="sm"
          onClick={startCheckout}
          disabled={busy}
          data-testid="trial-upgrade-cta"
          className="bg-amber-600 hover:bg-amber-500 text-white"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          {busy ? 'Opening checkout…' : 'Upgrade now'}
        </Button>
      </div>
    </div>
  );
}
